import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import type { VanFeatureProperties } from '@/components/map/utils/vanFeatureProperties';
import { toLngLatTuple } from '@/utils/mapUtils';

export interface VanInteractionCallbacks {
    /** A van marker was clicked. `coords` is the feature's Point [lng, lat] when available. */
    onVanClick?: (van: VanFeatureProperties, coords: [number, number] | null) => void;
}

interface VanInteractions {
    hoveredVan: VanFeatureProperties | null;
    hoveredVanLngLat: [number, number] | null;
}

/**
 * Hover and click interactions for the technician-vans layer: exposes the hovered van for
 * the hover card, and delegates clicks to the caller (which selects the van and opens the
 * details panel). The global click classifier treats a 'van' outcome as owned here.
 */
export function useVanInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    callbacks?: VanInteractionCallbacks,
): VanInteractions {
    const [hoveredVan, setHoveredVan] = useState<VanFeatureProperties | null>(null);
    const [hoveredVanLngLat, setHoveredVanLngLat] = useState<[number, number] | null>(null);
    const hoveredIdRef = useRef<string | null>(null);

    // Ref-held so the effect never re-runs (and re-registers listeners) on a new
    // callbacks object identity.
    const callbacksRef = useRef(callbacks);
    useLayoutEffect(() => {
        callbacksRef.current = callbacks;
    });

    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const clearHover = () => {
            if (hoveredIdRef.current === null) return;
            hoveredIdRef.current = null;
            setHoveredVan(null);
            setHoveredVanLngLat(null);
        };

        const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            // No hover while the camera moves — a card created mid-drag/zoom/fly would
            // pan with the map, anchored to a point sliding out from under the cursor.
            if (mlMap.isMoving()) return;
            const feature = e.features?.[0];
            if (!feature) return;

            const props = feature.properties as unknown as VanFeatureProperties;
            if (props.id === hoveredIdRef.current) return;

            hoveredIdRef.current = props.id;
            const geometry = feature.geometry;
            const lngLat: [number, number] =
                geometry.type === 'Point' ? (geometry.coordinates as [number, number]) : [e.lngLat.lng, e.lngLat.lat];
            setHoveredVan(props);
            setHoveredVanLngLat(lngLat);
        };

        const handleMoveStart = () => {
            clearHover();
        };

        const handleVanClick = (e: maplibregl.MapLayerMouseEvent) => {
            const features = mlMap.queryRenderedFeatures(e.point, { layers: [ORBIT_LAYER_IDS.technicianVans] });
            if (!features.length) return;

            const props = features[0].properties as unknown as VanFeatureProperties;
            // Dismiss hover before the click opens the details panel.
            clearHover();
            const coords =
                features[0].geometry.type === 'Point'
                    ? (toLngLatTuple(features[0].geometry.coordinates) ?? null)
                    : null;
            callbacksRef.current?.onVanClick?.(props, coords);
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        mlMap.on('mousemove', ORBIT_LAYER_IDS.technicianVans, handleMouseMove);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.technicianVans, clearHover);
        mlMap.on('click', ORBIT_LAYER_IDS.technicianVans, handleVanClick);
        mlMap.on('movestart', handleMoveStart);

        return () => {
            mlMap.off('mousemove', ORBIT_LAYER_IDS.technicianVans, handleMouseMove);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.technicianVans, clearHover);
            mlMap.off('click', ORBIT_LAYER_IDS.technicianVans, handleVanClick);
            mlMap.off('movestart', handleMoveStart);
        };
    }, [isMapLoaded, mapRef]);

    return { hoveredVan, hoveredVanLngLat };
}
